import React, { useEffect, useState, useRef } from "react";
import { Typography } from "@mui/material";
import { motion, useInView } from "framer-motion";

const AnimatedCounter = ({ 
  end, 
  duration = 2, 
  suffix = "", 
  prefix = "",
  sx = {},
  ...props 
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      // Parse the end value to handle numbers with decimals and special characters
      const numericEnd = parseFloat(end.toString().replace(/[^0-9.]/g, ''));
      const currentCount = easeOutQuart * numericEnd;
      
      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(numericEnd);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isInView, end, duration]);

  // Format the number based on the original end value format
  const formatNumber = (num) => {
    const endStr = end.toString();
    
    // Check if original has decimal
    if (endStr.includes('.')) {
      return num.toFixed(1);
    }
    
    // Check if original has K, M, etc.
    if (endStr.includes('K') || endStr.includes('k')) {
      return Math.round(num) + 'K';
    }
    
    if (endStr.includes('M') || endStr.includes('m')) {
      return Math.round(num) + 'M';
    }

    if (endStr.includes('X') || endStr.includes('x')) {
      return Math.round(num) + 'X';
    }
    
    return Math.round(num);
  };

  return (
    <Typography
      ref={ref}
      component={motion.span}
      {...props}
      sx={sx}
    >
      {prefix}{formatNumber(count)}{suffix}
    </Typography>
  );
};

export default AnimatedCounter;